<template>
    <div class="flex items-center space-x-2 mr-6">
        <button
            v-if="!displayFullBlock"
            class="px-4 py-2 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 transition"
            @click="setLast30Days"
        >
            Last 30 days
        </button>

        <div
            :class="[
                'relative',
                !hasDateRangeAccess && 'cursor-pointer opacity-50',
            ]"
            @click="!hasDateRangeAccess && checkAccessOrShowModal()"
        >
            <DateRangePicker
                v-model="selectedDateRange"
                :display-full-block="displayFullBlock"
                @update:model-value="handleDateRangeChange"
            />

            <div
                v-if="!hasDateRangeAccess"
                class="absolute inset-0 bg-transparent cursor-pointer"
                title="Click for upgrade options - Scale plan required"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { parseDate, today } from "@internationalized/date";
import { computed, type PropType, ref } from "vue";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import useDateRangeAccess from "@/composables/useDateRangeAccess.ts";
const { hasDateRangeAccess, checkAccessOrShowModal } = useDateRangeAccess();

function subtractDays(date, days: number) {
    const js = new Date(date.toString());
    js.setDate(js.getDate() - days);
    return parseDate(js.toISOString().slice(0, 10));
}

const setLast30Days = () => {
    if (!checkAccessOrShowModal()) return;

    const end = today("utc");
    const start = subtractDays(end, 30);

    selectedDateRange.value = { start, end };
    handleDateRangeChange({ start, end });
};

interface ReportedDomain {
    domain: string;
    dmarcPolicy: string;
}

const props = defineProps({
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
    dateRange: {
        type: Object as PropType<{
            start: string;
            end: string;
        }>,
        required: true,
    },
    displayFullBlock: Boolean,
    routeName: {
        type: String,
        default: "reports.show",
    },
    routeParams: {
        type: Object as PropType<Record<string, string>>,
        default: null,
    },
});

const emit = defineEmits(["changed"]);

const selectedDateRange = ref<DateRange>({
    start: parseDate(props.dateRange.start),
    end: parseDate(props.dateRange.end),
});

const resolvedRouteParams = computed(() => {
    if (props.routeParams) {
        return props.routeParams;
    }

    return { domain: props.reportedDomain.domain };
});

const handleDateRangeChange = (newRange: DateRange) => {
    if (!checkAccessOrShowModal()) {
        return;
    }

    if (!newRange.start || !newRange.end) {
        return;
    }

    const startDate = newRange.start.toString();
    const endDate = newRange.end.toString();

    router.get(
        route(props.routeName, resolvedRouteParams.value),
        {
            start_date: startDate,
            end_date: endDate,
        },
        {
            preserveState: false,
            preserveScroll: false,
        },
    );
};
</script>
